const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface CategoryKPI {
  id?: number;
  categoryId: number;
  kpiName: string;
  kpiCode: string;
  measurementType: string;
  description?: string;
  type: string;
  abbreviation: string;
  typeDescription?: string;
  isActive: boolean;
  displayOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateCategoryKPIRequest {
  categoryId: number;
  kpiName: string;
  kpiCode: string;
  measurementType: string;
  description?: string;
  type: string;
  displayOrder?: number;
}

export interface UpdateCategoryKPIRequest {
  kpiName?: string;
  kpiCode?: string;
  measurementType?: string;
  description?: string;
  type?: string;
  displayOrder?: number;
}

class CategoryKPIService {
  private async getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  async getCategoryKPIs(categoryId: number): Promise<CategoryKPI[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/category-kpis/category/${categoryId}`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch category KPIs: ${response.statusText}`);
      }

      const data = await response.json();
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching category KPIs:', error);
      throw error;
    }
  }

  async createCategoryKPI(kpiData: CreateCategoryKPIRequest): Promise<CategoryKPI> {
    try {
      const response = await fetch(`${API_BASE_URL}/category-kpis`, {
        method: 'POST',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(kpiData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to create KPI: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to create KPI');
      }

      return data.data;
    } catch (error) {
      console.error('Error creating category KPI:', error);
      throw error;
    }
  }

  async updateCategoryKPI(id: number, kpiData: UpdateCategoryKPIRequest): Promise<CategoryKPI> {
    try {
      const response = await fetch(`${API_BASE_URL}/category-kpis/${id}`, {
        method: 'PUT',
        headers: await this.getAuthHeaders(),
        body: JSON.stringify(kpiData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update KPI: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to update KPI');
      }

      return data.data;
    } catch (error) {
      console.error('Error updating category KPI:', error);
      throw error;
    }
  }

  async deleteCategoryKPI(id: number): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/category-kpis/${id}`, {
        method: 'DELETE',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete KPI: ${response.statusText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to delete KPI');
      }
    } catch (error) {
      console.error('Error deleting category KPI:', error);
      throw error;
    }
  }

  async getMeasurementTypes(): Promise<string[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/category-kpis/measurement-types`, {
        method: 'GET',
        headers: await this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch measurement types: ${response.statusText}`);
      }

      const data = await response.json();
      return data.success ? data.data : [];
    } catch (error) {
      console.error('Error fetching measurement types:', error);
      throw error;
    }
  }
}

export default new CategoryKPIService();